import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LEARNABLE_SKILLS,
  SKILL_NAMES,
  TASK_AREAS,
  TASK_AREA_NAMES,
  type MeDto,
  type SkillId,
  type TaskArea,
  type TaskDto,
} from '@setu/shared';
import { api, api2, ApiError, type NoteDto } from './api.ts';
import { useInviteToken, useResetToken, useRoute } from './useRoute.ts';
import { ResetPassword } from './screens/PasswordHelp.tsx';
import Unsubscribe from './screens/Unsubscribe.tsx';
import SignIn from './screens/SignIn.tsx';
import AcceptInvite, { HOURS } from './screens/AcceptInvite.tsx';
import TaskCard from './components/TaskCard.tsx';
import Campaigns from './screens/Campaigns.tsx';
import Messages from './screens/Messages.tsx';
import Ideas from './screens/Ideas.tsx';
import Toolkit from './screens/Toolkit.tsx';
import Team from './screens/Team.tsx';

type Tab = 'home' | 'help' | 'campaigns' | 'ideas' | 'toolkit' | 'messages' | 'team' | 'you';

const TABS: { id: Tab; label: string; short: string }[] = [
  { id: 'home', label: 'Home', short: 'Home' },
  { id: 'help', label: 'How you can help', short: 'Help' },
  { id: 'campaigns', label: 'Campaigns', short: 'Campaigns' },
  { id: 'ideas', label: 'Ideas', short: 'Ideas' },
  { id: 'toolkit', label: 'Toolkit', short: 'Toolkit' },
  { id: 'messages', label: 'Messages', short: 'Messages' },
  { id: 'team', label: 'You & the team', short: 'Team' },
  { id: 'you', label: 'Your profile', short: 'You' },
];

/** What the phone's bottom bar shows outright; everything else is under More. */
const PHONE_PRIMARY: Tab[] = ['home', 'help', 'campaigns', 'messages'];

export default function App(): React.ReactElement {
  const { path, go } = useRoute();
  const [user, setUser] = useState<MeDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .me()
      .then(({ user }) => setUser(user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  // The token lives in the fragment so it is never sent to any server.
  const inviteToken = useInviteToken(path);
  const resetToken = useResetToken(path);

  if (inviteToken) {
    return (
      <AcceptInvite
        token={inviteToken}
        onAccepted={(u) => {
          setUser(u);
          // Drop the token out of the address bar the moment it is spent.
          window.history.replaceState({}, '', '/');
          go('/');
        }}
      />
    );
  }

  // Works signed in or not: the link in the email is the whole credential.
  if (path.startsWith('/unsubscribe')) return <Unsubscribe onDone={() => go('/')} />;

  if (resetToken) {
    const leave = (u: MeDto | null) => {
      if (u) setUser(u);
      // Drop the token out of the address bar the moment it is spent.
      window.history.replaceState({}, '', '/');
      go('/');
    };
    return <ResetPassword token={resetToken} onDone={leave} onGiveUp={() => leave(null)} />;
  }

  if (loading) {
    return (
      <div className="gate">
        <p className="muted">Opening Setu…</p>
      </div>
    );
  }

  if (!user) return <SignIn onSignedIn={setUser} />;

  return <Shell user={user} onUser={setUser} />;
}

/* ------------------------------------------------------------------ shell */

function Shell({ user, onUser }: { user: MeDto; onUser: (u: MeDto | null) => void }): React.ReactElement {
  const [tab, setTab] = useState<Tab>('home');
  const [moreOpen, setMoreOpen] = useState(false);
  const [open, setOpen] = useState<TaskDto[]>([]);
  const [mine, setMine] = useState<TaskDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cardErrors, setCardErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<NoteDto[]>([]);
  const [unread, setUnread] = useState(0);

  const lastLoaded = useRef(0);

  const refresh = useCallback(async () => {
    lastLoaded.current = Date.now();
    try {
      const [openRes, mineRes, notesRes, threadsRes] = await Promise.all([
        api.openTasks(),
        api.myTasks(),
        api2.notes().catch(() => ({ notes: [] })),
        api2.threads().catch(() => ({ threads: [] })),
      ]);
      setOpen(openRes.tasks);
      setMine(mineRes.tasks);
      setNotes(notesRes.notes);
      setUnread(threadsRes.threads.reduce((sum, t) => sum + t.unread, 0));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your work.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /**
   * A task published while the app sits open should not have to wait for a
   * full reload. Re-read on every tab change and whenever the window comes
   * back into focus — but never more than once every fifteen seconds, so
   * flicking between tabs doesn't hammer the server.
   */
  const refreshIfStale = useCallback(() => {
    if (Date.now() - lastLoaded.current < 15_000) return;
    void refresh();
  }, [refresh]);

  useEffect(() => {
    window.addEventListener('focus', refreshIfStale);
    return () => window.removeEventListener('focus', refreshIfStale);
  }, [refreshIfStale]);

  const openTab = useCallback(
    (next: Tab) => {
      setTab(next);
      refreshIfStale();
    },
    [refreshIfStale],
  );

  /**
   * Every action re-reads both lists rather than patching state in place: a
   * task can fill up between the list loading and the tap landing, and the
   * honest answer to that is to show what is actually true now.
   */
  const act = useCallback(
    async (taskId: string, action: string, extra?: { note?: string; proof?: string; proofFileId?: string }) => {
      try {
        setCardErrors((prev) => {
          const next = { ...prev };
          delete next[taskId];
          return next;
        });
        await api.actOnTask(taskId, action, extra ?? {});
        await refresh();
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'That did not go through.';
        setCardErrors((prev) => ({ ...prev, [taskId]: message }));
        // A conflict means our picture is stale — get the true one.
        if (err instanceof ApiError && err.status === 409) await refresh();
      }
    },
    [refresh],
  );

  const accepted = useMemo(() => mine.filter((t) => t.myStatus === 'accepted'), [mine]);
  const completed = useMemo(() => mine.filter((t) => t.myStatus === 'completed'), [mine]);

  const signOut = async () => {
    await api.logout().catch(() => undefined);
    onUser(null);
  };

  const body = (() => {
    if (loading) return <p className="muted">Loading…</p>;
    if (error) {
      return (
        <div className="stack">
          <p className="notice alert" role="alert">
            {error}
          </p>
          <div>
            <button className="btn quiet" onClick={() => void refresh()}>
              Try again
            </button>
          </div>
        </div>
      );
    }
    switch (tab) {
      case 'help':
        return <Help open={open} act={act} cardErrors={cardErrors} />;
      case 'campaigns':
        return <Campaigns />;
      case 'ideas':
        return <Ideas cityName={user.cityName} />;
      case 'toolkit':
        return <Toolkit />;
      case 'messages':
        return <Messages />;
      case 'team':
        return <Team />;
      case 'you':
        return <You user={user} onUser={onUser} completed={completed.length} onSignOut={signOut} />;
      case 'home':
      default:
        return (
          <Home
            user={user}
            open={open}
            accepted={accepted}
            completed={completed}
            notes={notes}
            act={act}
            cardErrors={cardErrors}
            onSeeAll={() => openTab('help')}
            onNoteRead={(id) => {
              void api2.markNoteRead(id).catch(() => undefined);
              setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)));
            }}
          />
        );
    }
  })();

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <b>Setu</b>
          <span>the bridge</span>
        </div>
        <nav className="nav" aria-label="Sections">
          {TABS.map((t) => (
            <button key={t.id} aria-current={tab === t.id ? 'page' : undefined} onClick={() => openTab(t.id)}>
              {t.label}
              {t.id === 'help' && open.length > 0 && <span className="count">{open.length}</span>}
              {t.id === 'home' && accepted.length > 0 && <span className="count">{accepted.length}</span>}
              {t.id === 'messages' && unread > 0 && <span className="count">{unread}</span>}
            </button>
          ))}
        </nav>
        <div className="whoami">
          <b>{user.name}</b>
          <span>{user.cityName ? `Volunteer · ${user.cityName}` : 'Volunteer'}</span>
        </div>
      </aside>

      <main className="main">{body}</main>

      {/*
        Four everyday tabs and More. Eight tabs in a sideways-scrolling bar hid
        "Team" and "You" past the edge with nothing to say they were there.
      */}
      {moreOpen && (
        <div className="moresheet" role="dialog" aria-label="More sections">
          <button className="moresheet-backdrop" aria-label="Close" onClick={() => setMoreOpen(false)} />
          <div className="moresheet-panel">
            {TABS.filter((t) => !PHONE_PRIMARY.includes(t.id)).map((t) => (
              <button
                key={t.id}
                aria-current={tab === t.id ? 'page' : undefined}
                onClick={() => {
                  setMoreOpen(false);
                  openTab(t.id);
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}
      <nav className="tabbar" aria-label="Sections">
        {TABS.filter((t) => PHONE_PRIMARY.includes(t.id)).map((t) => (
          <button
            key={t.id}
            aria-current={tab === t.id && !moreOpen ? 'page' : undefined}
            aria-label={t.label}
            onClick={() => {
              setMoreOpen(false);
              openTab(t.id);
            }}
          >
            {t.short}
            {t.id === 'help' && open.length > 0 && <span className="dot" aria-hidden="true" />}
            {t.id === 'messages' && unread > 0 && <span className="dot" aria-hidden="true" />}
          </button>
        ))}
        <button
          aria-expanded={moreOpen}
          aria-current={moreOpen || !PHONE_PRIMARY.includes(tab) ? 'page' : undefined}
          onClick={() => setMoreOpen((o) => !o)}
        >
          More
        </button>
      </nav>
    </div>
  );
}

/* ------------------------------------------------------------------- home */

function Home({
  user,
  open,
  accepted,
  completed,
  notes,
  act,
  cardErrors,
  onSeeAll,
  onNoteRead,
}: {
  user: MeDto;
  open: TaskDto[];
  accepted: TaskDto[];
  completed: TaskDto[];
  notes: NoteDto[];
  act: (id: string, action: string, extra?: { note?: string; proof?: string }) => Promise<void>;
  cardErrors: Record<string, string>;
  onSeeAll: () => void;
  onNoteRead: (id: string) => void;
}): React.ReactElement {
  // Home stays deliberately short: it leads with the numbers, shows at most
  // two things that need this person, and hands off to the tabs.
  const nextUp = open.slice(0, 2);

  return (
    <div className="stack-lg">
      <header>
        <h1>{greeting()}, {firstName(user.name)}.</h1>
        <p className="soft" style={{ marginTop: 6 }}>
          Today, this is your part of it.
        </p>
      </header>

      {/* Notes written to this volunteer by name and signed. Not a broadcast,
          and never in an inbox — this is the whole point of them. */}
      {notes.length > 0 && (
        <section className="stack">
          <h2>For you</h2>
          {notes.slice(0, 3).map((note) => (
            <article
              key={note.id}
              className="card stack"
              style={{ gap: 8, borderLeft: '4px solid var(--marigold)' }}
              onMouseEnter={() => !note.readAt && onNoteRead(note.id)}
            >
              <p>{note.body}</p>
              <p className="small muted">
                — {note.fromName}, {note.fromRole}
              </p>
            </article>
          ))}
        </section>
      )}

      <section className="tiles" aria-label="Where things stand">
        <div className="tile marigold">
          <b>{open.length}</b>
          <span>{open.length === 1 ? 'thing waiting for you' : 'things waiting for you'}</span>
        </div>
        <div className="tile indigo">
          <b>{accepted.length}</b>
          <span>you said yes to</span>
        </div>
        <div className="tile mint">
          <b>{completed.length}</b>
          <span>finished</span>
        </div>
      </section>

      <section className="stack">
        <h2>Yours this week</h2>
        {accepted.length === 0 ? (
          <div className="card empty">
            <p>Suspiciously peaceful in here.</p>
            <button className="btn quiet" style={{ marginTop: 14 }} onClick={onSeeAll}>
              See what needs doing
            </button>
          </div>
        ) : (
          accepted.map((task) => (
            <TaskCard key={task.id} task={task} error={cardErrors[task.id]} onAct={(a, e) => act(task.id, a, e)} />
          ))
        )}
      </section>

      {nextUp.length > 0 && (
        <section className="stack">
          <div className="row">
            <h2>What needs you</h2>
            {open.length > nextUp.length && (
              <button className="btn ghost" style={{ marginLeft: 'auto' }} onClick={onSeeAll}>
                See all {open.length}
              </button>
            )}
          </div>
          {nextUp.map((task) => (
            <TaskCard key={task.id} task={task} error={cardErrors[task.id]} onAct={(a, e) => act(task.id, a, e)} />
          ))}
        </section>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- help */

function Help({
  open,
  act,
  cardErrors,
}: {
  open: TaskDto[];
  act: (id: string, action: string, extra?: { note?: string; proof?: string }) => Promise<void>;
  cardErrors: Record<string, string>;
}): React.ReactElement {
  const [area, setArea] = useState<TaskArea | null>(null);

  // Only offer filters for areas that actually have something in them —
  // a chip that yields an empty list is a small lie.
  const areasPresent = useMemo(() => TASK_AREAS.filter((a) => open.some((t) => t.area === a)), [open]);
  const shown = area ? open.filter((t) => t.area === area) : open;

  return (
    <div className="stack-lg">
      <header>
        <h1>How you can help</h1>
        <p className="soft" style={{ marginTop: 6 }}>
          Matched to what you said you can do, and to the time you have.
        </p>
      </header>

      {areasPresent.length > 1 && (
        <div className="chips" role="group" aria-label="Filter by kind of work">
          <button className="chip" aria-pressed={area === null} onClick={() => setArea(null)}>
            Everything
          </button>
          {areasPresent.map((a) => (
            <button key={a} className="chip" aria-pressed={area === a} onClick={() => setArea(a)}>
              {TASK_AREA_NAMES[a]}
            </button>
          ))}
        </div>
      )}

      {shown.length === 0 ? (
        <div className="card empty">
          <p>Nothing waiting for you right now.</p>
          <p className="small" style={{ marginTop: 8 }}>
            When your coordinator posts something that fits you, it lands here.
          </p>
        </div>
      ) : (
        <div className="stack">
          {shown.map((task) => (
            <TaskCard key={task.id} task={task} error={cardErrors[task.id]} onAct={(a, e) => act(task.id, a, e)} />
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- you */

function You({
  user,
  onUser,
  completed,
  onSignOut,
}: {
  user: MeDto;
  onUser: (u: MeDto) => void;
  completed: number;
  onSignOut: () => void;
}): React.ReactElement {
  const [skills, setSkills] = useState<SkillId[]>(user.skills);
  const [otherSkills, setOtherSkills] = useState(user.otherSkills);
  const [learn, setLearn] = useState<SkillId[]>(user.learn);
  const [weeklyMinutes, setWeeklyMinutes] = useState(user.weeklyMinutes);
  const [away, setAway] = useState(user.awayUntil ?? '');
  const [emailNewWork, setEmailNewWork] = useState(user.emailNewWork);
  const [emailIdeaDecisions, setEmailIdeaDecisions] = useState(user.emailIdeaDecisions);
  const [emailFrequency, setEmailFrequency] = useState(user.emailFrequency);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      const { user: updated } = await api.updateProfile({
        skills,
        otherSkills,
        learn,
        weeklyMinutes,
        awayUntil: away || null,
        emailNewWork,
        emailIdeaDecisions,
        emailFrequency,
      });
      onUser(updated);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (skill: SkillId) =>
    setSkills((prev) => (prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]));

  const toggleLearn = (skill: SkillId) =>
    setLearn((prev) => (prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]));

  return (
    <div className="stack-lg">
      <header>
        <h1>Your profile</h1>
        <p className="soft" style={{ marginTop: 6 }}>
          {user.cityName ? `Volunteering in ${user.cityName}.` : 'Volunteering with the promotions team.'}{' '}
          {completed > 0 ? `${completed} finished so far.` : ''}
        </p>
      </header>

      <section className="card stack">
        <div>
          <h2>What you can help with</h2>
          <p className="small muted">This is what decides which work is put in front of you.</p>
        </div>
        <div className="chips">
          {(Object.keys(SKILL_NAMES) as SkillId[]).map((skill) => (
            <button key={skill} className="chip" aria-pressed={skills.includes(skill)} onClick={() => toggle(skill)}>
              {SKILL_NAMES[skill]}
            </button>
          ))}
        </div>
        <div className="field">
          <label htmlFor="other-skills">Other</label>
          <input
            id="other-skills"
            type="text"
            maxLength={200}
            value={otherSkills}
            onChange={(e) => setOtherSkills(e.target.value)}
          />
          <span className="hint">Something you're good at that isn't listed.</span>
        </div>
        <div>
          <h2>I'd like to learn</h2>
          <p className="small muted">Optional. We'll point you at the playbooks and pair you up.</p>
        </div>
        <div className="chips">
          {LEARNABLE_SKILLS.map((skill) => (
            <button
              key={skill}
              className="chip"
              aria-pressed={learn.includes(skill)}
              onClick={() => toggleLearn(skill)}
            >
              {SKILL_NAMES[skill]}
            </button>
          ))}
        </div>
      </section>

      <section className="card stack">
        <div>
          <h2>Honestly, how much time?</h2>
          <p className="small muted">
            You answered this when you joined — life changes, so change it here. Nothing longer than this will be
            put in front of you.
          </p>
        </div>
        <div className="chips">
          {HOURS.map((option) => (
            <button
              key={option.label}
              className="chip"
              aria-pressed={weeklyMinutes === option.minutes}
              onClick={() => setWeeklyMinutes(option.minutes)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </section>

      <section className="card stack">
        <div>
          <h2>Taking a break?</h2>
          <p className="small muted">
            Set a date and nothing will be asked of you until then. Your coordinator sees it, so nobody chases you.
          </p>
        </div>
        <div className="field">
          <label htmlFor="away">Back on</label>
          <input id="away" type="date" value={away} onChange={(e) => setAway(e.target.value)} />
        </div>
        {away && (
          <div>
            <button className="btn ghost" onClick={() => setAway('')}>
              Actually, I'm around
            </button>
          </div>
        )}
      </section>

      <section className="card stack">
        <div>
          <h2>Emails from Setu</h2>
          <p className="small muted">
            Unless you choose otherwise, Setu emails you once a week. Everything also shows up here in the app, so
            turning an email off never means missing something — only not hearing about it in your inbox.
          </p>
        </div>
        <label className="check">
          <input type="checkbox" checked={emailNewWork} onChange={(e) => setEmailNewWork(e.target.checked)} />
          <span>When new work is posted that matches me</span>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={emailIdeaDecisions}
            onChange={(e) => setEmailIdeaDecisions(e.target.checked)}
          />
          <span>When there is a decision on an idea I pitched</span>
        </label>
        {(emailNewWork || emailIdeaDecisions) && (
          <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0, gap: 0 }}>
            <legend className="small muted" style={{ padding: 0, marginBottom: 4 }}>
              How often
            </legend>
            {(
              [
                ['weekly', 'One email a week, on Monday morning'],
                ['daily', 'One email a day, in the morning'],
                ['instant', 'As it happens — one email each time'],
              ] as const
            ).map(([value, label]) => (
              <label className="check" key={value}>
                <input
                  type="radio"
                  name="email-frequency"
                  checked={emailFrequency === value}
                  onChange={() => setEmailFrequency(value)}
                />
                <span>{label}</span>
              </label>
            ))}
          </fieldset>
        )}
      </section>

      {error && (
        <p className="notice alert" role="alert">
          {error}
        </p>
      )}
      {saved && <p className="notice mint">Saved.</p>}

      <div className="row">
        <button className="btn" onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button className="btn quiet" onClick={onSignOut}>
          Sign out
        </button>
      </div>

      <p className="small muted">Signed in as {user.email}.</p>
    </div>
  );
}

/* ---------------------------------------------------------------- helpers */

function firstName(name: string): string {
  return name.split(' ')[0] ?? name;
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}
